import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { submitContactForm } from '../mockData';
import { useSiteSettings } from '../hooks/use-site-settings';
import { useSectionsContent } from '../hooks/use-sections-content';
import { useLanguage } from '../i18n/LanguageContext';

const Contact = () => {
  const { toast } = useToast();
  const { siteSettings } = useSiteSettings();
  const { sectionsContent } = useSectionsContent();
  const { tr, localizeText } = useLanguage();
  const copy = sectionsContent?.publicPageCopy || {};
  const text = (key, fallback) => copy[key] || fallback;

  const resources = Array.isArray(sectionsContent?.resources) ? sectionsContent.resources : [];
  const officeHoursWeekdayLabel = localizeText(siteSettings.officeHoursWeekdayLabel) || tr('contact.office.monFri');
  const officeHoursWeekdayHours = localizeText(siteSettings.officeHoursWeekdayHours) || tr('contact.office.monFriHours');
  const officeHoursSaturdayLabel = localizeText(siteSettings.officeHoursSaturdayLabel) || tr('contact.office.sat');
  const officeHoursSaturdayHours = localizeText(siteSettings.officeHoursSaturdayHours) || tr('contact.office.satHours');
  const officeHoursSundayLabel = localizeText(siteSettings.officeHoursSundayLabel) || tr('contact.office.sun');
  const officeHoursSundayHours = localizeText(siteSettings.officeHoursSundayHours) || tr('contact.office.sunHours');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: tr('contact.toast.missingTitle'),
        description: tr('contact.toast.missingDescription'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactForm(formData);

      toast({
        title: tr('contact.toast.successTitle'),
        description: tr('contact.toast.successDescription'),
      });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      toast({
        title: tr('contact.toast.errorTitle'),
        description: tr('contact.toast.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-sage-600 to-earth-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-4">{text('contactHeroTitle', tr('contact.hero.title'))}</h1>
          <p className="text-xl text-sage-100 max-w-2xl mx-auto">
            {text('contactHeroSubtitle', tr('contact.hero.subtitle'))}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="border-sage-200 shadow-xl">
                <CardHeader className="bg-white border-b border-sage-200">
                  <CardTitle className="text-2xl text-sage-900">{text('contactFormTitle', tr('contact.form.title'))}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 bg-white">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-sage-700">{tr('contact.form.name')}</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder={tr('contact.form.placeholder.name')}
                          value={formData.name}
                          onChange={(event) => handleChange('name', event.target.value)}
                          className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-sage-700">{tr('contact.form.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder={tr('contact.form.placeholder.email')}
                          value={formData.email}
                          onChange={(event) => handleChange('email', event.target.value)}
                          className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-sage-700">{tr('contact.form.subject')}</Label>
                      <Input
                        id="subject"
                        type="text"
                        placeholder={tr('contact.form.placeholder.subject')}
                        value={formData.subject}
                        onChange={(event) => handleChange('subject', event.target.value)}
                        className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sage-700">{tr('contact.form.message')}</Label>
                      <Textarea
                        id="message"
                        placeholder={tr('contact.form.placeholder.message')}
                        value={formData.message}
                        onChange={(event) => handleChange('message', event.target.value)}
                        className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500 min-h-40"
                        rows={6}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-sage-600 hover:bg-sage-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-200 group"
                    >
                      {isSubmitting ? (
                        tr('contact.form.sending')
                      ) : (
                        <>
                          {tr('contact.form.send')}
                          <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-sage-200 shadow-lg bg-white">
                <CardHeader className="bg-sage-50 border-b border-sage-200">
                  <CardTitle className="text-lg text-sage-900">{tr('contact.info.title')}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-sage-200 transition-colors">
                      <Phone className="w-5 h-5 text-sage-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-900 mb-1">{tr('contact.info.phone')}</h3>
                      <a href={siteSettings.contactPhoneHref} className="text-sage-600 hover:text-sage-800 transition-colors">
                        {siteSettings.contactPhone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-sage-200 transition-colors">
                      <Mail className="w-5 h-5 text-sage-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-900 mb-1">{tr('contact.info.email')}</h3>
                      <a href={`mailto:${siteSettings.contactEmail}`} className="text-sage-600 hover:text-sage-800 transition-colors break-all">
                        {siteSettings.contactEmail}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-sage-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-900 mb-1">{tr('contact.info.location')}</h3>
                      <p className="text-sage-600">{localizeText(siteSettings.locationLabel)}</p>
                      <p className="text-sm text-sage-500 mt-1">{localizeText(siteSettings.serviceAreaLabel)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-sage-200 shadow-lg bg-sage-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-900 mb-3">{tr('contact.response.title')}</h3>
                  <p className="text-sm text-sage-700 leading-relaxed">
                    {tr('contact.response.text')}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-sage-200 shadow-lg bg-white">
                <CardHeader className="bg-sage-50 border-b border-sage-200">
                  <CardTitle className="text-lg text-sage-900">{tr('contact.office.title')}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-sage-700 font-medium">{officeHoursWeekdayLabel}</span>
                    <span className="text-sage-600">{officeHoursWeekdayHours}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sage-700 font-medium">{officeHoursSaturdayLabel}</span>
                    <span className="text-sage-600">{officeHoursSaturdayHours}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sage-700 font-medium">{officeHoursSundayLabel}</span>
                    <span className="text-sage-600">{officeHoursSundayHours}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-sage-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-sage-900 mb-8 text-center">{tr('contact.resources.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {resources.map((resource, index) => (
              <Card key={index} className="border-sage-200 bg-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-sage-900 mb-2">{localizeText(resource.title)}</h3>
                  <p className="text-sage-600 mb-4">{localizeText(resource.description)}</p>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sage-600 hover:text-sage-800 font-medium inline-flex items-center group"
                  >
                    {tr('contact.resources.visit')}
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
